import React, {useEffect} from "react";
import PayPalExpressButtons from "./PayPalExpressButtons";
import {DISPATCH_ACTION, usePayPalScriptReducer} from "@paypal/react-paypal-js";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import PayPalSingleButton from "./PayPalSingleButton";
import ErrorBoundary from "../../../utils/ErrorBoundary";
import {ButtonPropType} from "./helpers/paypalTypes";

type BraintreeButtonType = ButtonPropType & {isExpress: boolean};

/**
 * This component mainly checks whether there is a braintree token or not, and if so,
 * it dispatches a `resetOptions` with the braintree token and th derived client id
 * @param props properties of type `ButtonPropType`
 * @constructor
 */
const BraintreeButton: React.FC<BraintreeButtonType> = (props) => {
  const [{options, isResolved}, dispatch] = usePayPalScriptReducer();
  const {braintreeData} = useCartContext();

  useEffect(() => {
    if (braintreeData) {
      const prefix = (props.isExpress ? "PPX" : "PP") + " dispatch";
      if (!options.dataClientToken) {
        try {
          dispatch({
            type: DISPATCH_ACTION.RESET_OPTIONS,
            value: {
              ...options,
              dataClientToken: braintreeData.token,
              commit: !props.isExpress,
            },
          });
        } catch (e) {
          props.handleError(e as Error, prefix + " set data client token");
        }
      } else if (props.isExpress === options.commit) {
        try {
          dispatch({
            type: DISPATCH_ACTION.RESET_OPTIONS,
            value: {
              ...options,
              commit: !props.isExpress,
            },
          });
        } catch (e) {
          props.handleError(e as Error, prefix + " toggle commit");
        }
      }
    }
  }, [braintreeData, options.dataClientToken]);

  // Did we retrieve the braintree token? if so, has the dispatch finished the `resetOptions`
  if (!braintreeData || !options.dataClientToken || props.isExpress === options.commit || !isResolved) {
    // It is "bottom-margin-double" just for differing of this spinner and the spinner in the "PayPalSingleButton"
    // can be reverted to "bottom-margin", when FE-101468 is done
    return <PayPalLoadingSpinner classes="bottom-margin-double" />;
  }

  return (
    <ErrorBoundary
      handleError={props.handleError}
      fallback={null}
      occurrence={`PayPal${props.isExpress ? "Express" : ""} Error boundary`}
    >
      {props.isExpress ? <PayPalExpressButtons {...props} /> : <PayPalSingleButton {...props} />}
    </ErrorBoundary>
  );
};

export default BraintreeButton;

/**
 * Loading spinner just for the PayPal checkout buttons
 * @constructor
 */
export const PayPalLoadingSpinner: React.FC<{classes?: string; background?: "dark" | "bright"}> = ({
  classes,
  background = "bright",
}) => {
  return (
    <div className={`paypal-loading-div${classes ? ` ${classes}` : ""}`}>
      <div className={`swiper-lazy-preloader${background === "bright" ? "" : " swiper-lazy-preloader-white"}`}>
        {/* empty div for loading spinner */}
      </div>
      &nbsp;
    </div>
  );
};

/**
 * [Styling options](https://developer.paypal.com/docs/business/checkout/reference/style-guide/#customize-the-payment-buttons) for customizing the button appearance.
 */
type PayPalLabelType = "checkout" | "paypal";
type PayPalLayoutType = "vertical" | "horizontal";
export const buttonStyleObjectExpress = {
  height: 40,
  label: "checkout" as PayPalLabelType,
  layout: "vertical" as PayPalLayoutType,
};

export const buttonStyleObjectSummary = {
  ...buttonStyleObjectExpress,
  label: "paypal" as PayPalLabelType,
  layout: "horizontal" as PayPalLayoutType,
  tagline: false,
};
